/**
 * Color Palette Generator Tool
 *
 * Generate color palettes from an input color using culori.
 */

import type { CuloriOklch } from 'culori';
import { converter,formatHex, parse as parseColor } from 'culori';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type PaletteType = 'complementary' | 'analogous' | 'triadic' | 'split-complementary' | 'all';
type OutputFormat = 'hex' | 'css-variables' | 'json';

const toOklch = converter('oklch');

export const ColorPaletteGeneratorTool: ITransformerTool = {
  id: 'color-palette-generator',
  name: 'Color Palette Generator',
  description: 'Generate color palettes (complementary, analogous, triadic, split-complementary) from any color',
  version: '1.0.0',
  type: 'transformer',
  category: 'Generators',
  keywords: ['color', 'palette', 'complementary', 'analogous', 'triadic', 'generator', 'design'],

  seo: {
    slug: 'color-palette-generator',
    pageTitle: 'Color Palette Generator - Free Online Tool | Prism',
    metaDescription: 'Generate complementary, analogous, triadic, and split-complementary palettes from any color. Output as hex, CSS variables, or JSON. Fast and private.',
    keywords: ['color palette generator', 'complementary colors', 'analogous colors', 'triadic palette', 'color scheme generator'],
    relatedTools: ['color-converter', 'css-format', 'css-minify'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'paletteType',
      type: 'select',
      label: 'Palette Type',
      defaultValue: 'all',
      options: ['complementary', 'analogous', 'triadic', 'split-complementary', 'all'],
      description: 'Type of color harmony to generate',
    },
    {
      name: 'outputFormat',
      type: 'select',
      label: 'Output Format',
      defaultValue: 'hex',
      options: ['hex', 'css-variables', 'json'],
      description: 'Output format for the palette',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const colorStr = String(input ?? '').trim();
    if (!colorStr) return '';

    const paletteType = (config.paletteType as PaletteType) || 'all';
    const outputFormat = (config.outputFormat as OutputFormat) || 'hex';

    // Parse the first line as a color
    const firstLine = colorStr.split('\n')[0].trim();
    const parsed = parseColor(firstLine);

    if (!parsed) {
      throw ToolError.validation(`Cannot parse color: "${firstLine}"`, {
        suggestion: 'Enter a valid color like #ff6600, rgb(255, 102, 0), hsl(24, 100%, 50%), or a named color like "coral"',
      });
    }

    const baseHex = formatHex(parsed) ?? '#000000';
    const oklch = toOklch(parsed);

    if (!oklch || oklch.h === undefined) {
      throw ToolError.execution('Cannot convert color to OKLCH color space for palette generation');
    }

    const baseH = oklch.h ?? 0;
    const baseL = oklch.l;
    const baseC = oklch.c;

    const palettes: Record<string, string[]> = {};

    if (paletteType === 'complementary' || paletteType === 'all') {
      palettes['Complementary'] = [
        baseHex,
        hueRotate(baseL, baseC, baseH, 180),
      ];
    }

    if (paletteType === 'analogous' || paletteType === 'all') {
      palettes['Analogous'] = [
        hueRotate(baseL, baseC, baseH, -30),
        baseHex,
        hueRotate(baseL, baseC, baseH, 30),
      ];
    }

    if (paletteType === 'triadic' || paletteType === 'all') {
      palettes['Triadic'] = [
        baseHex,
        hueRotate(baseL, baseC, baseH, 120),
        hueRotate(baseL, baseC, baseH, 240),
      ];
    }

    if (paletteType === 'split-complementary' || paletteType === 'all') {
      palettes['Split-Complementary'] = [
        baseHex,
        hueRotate(baseL, baseC, baseH, 150),
        hueRotate(baseL, baseC, baseH, 210),
      ];
    }

    return formatOutput(palettes, baseHex, outputFormat);
  },
};

function hueRotate(l: number, c: number, h: number, degrees: number): string {
  const newH = ((h + degrees) % 360 + 360) % 360;
  const color: CuloriOklch = { mode: 'oklch', l, c, h: newH };
  return formatHex(color) ?? '#000000';
}

function formatOutput(palettes: Record<string, string[]>, baseHex: string, format: OutputFormat): string {
  if (format === 'json') {
    const obj: Record<string, unknown> = { base: baseHex };
    for (const [name, colors] of Object.entries(palettes)) {
      obj[name.toLowerCase().replace(/[- ]/g, '_')] = colors;
    }
    return JSON.stringify(obj, null, 2);
  }

  if (format === 'css-variables') {
    const lines: string[] = [':root {', `  --color-base: ${baseHex};`];
    for (const [name, colors] of Object.entries(palettes)) {
      const prefix = name.toLowerCase().replace(/[- ]/g, '-');
      colors.forEach((c, i) => {
        lines.push(`  --color-${prefix}-${i + 1}: ${c};`);
      });
    }
    lines.push('}');
    return lines.join('\n');
  }

  // hex list (default)
  const lines: string[] = [`Base: ${baseHex}`, ''];
  for (const [name, colors] of Object.entries(palettes)) {
    lines.push(`${name}:`);
    colors.forEach(c => lines.push(`  ${c}`));
    lines.push('');
  }
  return lines.join('\n').trimEnd();
}
